import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react'
import { createPortal } from 'react-dom'
import { FieldValues } from 'react-hook-form'
import {
  ConfirmDialog,
  FIELD_VALUE_CONSTANTS,
  Modal,
  TableActionType,
} from '@ui-kit'
import {
  DataTypeStore,
  fetchDuplicateRecordsAPI,
  getEntityDataByEntityFilter,
  getLayoutByEntityIdAndTypeApi,
  LayoutResponseType,
  applyProgramScopeToDuplicatePayload,
  transformToDuplicateApiPayload,
} from '@app-store'
import { DuplicatePopupModal } from './DuplicatePopupModal'
import { PatientAdvancedSearchPanel } from './PatientAdvancedSearchPanel'
import {
  BUTTONS,
  CASE_LEVEL_DEDUPE_ACTION,
  DUPLICATE_POPUP,
  LAYOUT_TYPE_NAMESPACE,
  NAMESPACES,
} from '../../utils/constants'
import { APPLICATION_PREFERENCES_STRINGS } from '../../utils/strings'
import {
  CaseLevelDedupeFlowModalProps,
  PatientAdvancedSearchPanelHandle,
} from '../../utils/types'
import { DYNAMIC_QUEUE_CONSTANTS } from '../../constants/dynamicQueue'

const CASE_LEVEL_DUPLICATE_ATTACH_KEY = 'case_level_duplicate_attach'

interface AttachCaseConfirmDialogHandle {
  open: (caseId: string | number) => void
  dismiss: () => void
  isOpen: () => boolean
}

interface AttachCaseConfirmDialogProps {
  onConfirm: (caseId: string | number) => void
}

const AttachCaseConfirmDialog = forwardRef<
  AttachCaseConfirmDialogHandle,
  AttachCaseConfirmDialogProps
>(({ onConfirm }, ref) => {
  const [open, setOpen] = useState(false)
  const openRef = useRef(false)
  const pendingCaseIdRef = useRef<string | number | null>(null)

  useImperativeHandle(ref, () => ({
    open: (caseId: string | number) => {
      pendingCaseIdRef.current = caseId
      openRef.current = true
      setOpen(true)
    },
    dismiss: () => {
      openRef.current = false
      setOpen(false)
      pendingCaseIdRef.current = null
    },
    isOpen: () => openRef.current,
  }))

  const handleCancel = () => {
    openRef.current = false
    setOpen(false)
    pendingCaseIdRef.current = null
  }

  const handleConfirm = () => {
    const caseId = pendingCaseIdRef.current
    openRef.current = false
    setOpen(false)
    pendingCaseIdRef.current = null

    if (caseId == null) {
      return
    }

    onConfirm(caseId)
  }

  return createPortal(
    <ConfirmDialog
      open={open}
      message={DUPLICATE_POPUP.ATTACH_CONFIRM_MESSAGE}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      cancelButtonText={NAMESPACES.CANCEL_BUTTON}
      confirmButtonText={NAMESPACES.YES}
    />,
    document.body
  )
})

AttachCaseConfirmDialog.displayName = 'AttachCaseConfirmDialog'

interface ConfirmDialogHandle {
  open: () => void
  dismiss: () => void
  isOpen: () => boolean
}

interface ProceedAnywayConfirmDialogProps {
  onConfirm: () => void
}

const ProceedAnywayConfirmDialog = forwardRef<
  ConfirmDialogHandle,
  ProceedAnywayConfirmDialogProps
>(({ onConfirm }, ref) => {
  const [open, setOpen] = useState(false)
  const openRef = useRef(false)

  useImperativeHandle(ref, () => ({
    open: () => {
      openRef.current = true
      setOpen(true)
    },
    dismiss: () => {
      openRef.current = false
      setOpen(false)
    },
    isOpen: () => openRef.current,
  }))

  const handleCancel = () => {
    openRef.current = false
    setOpen(false)
  }

  const handleConfirm = () => {
    openRef.current = false
    setOpen(false)
    onConfirm()
  }

  return createPortal(
    <ConfirmDialog
      open={open}
      message={DUPLICATE_POPUP.PROCEED_INTAKE_CONFIRM_MESSAGE}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      cancelButtonText={NAMESPACES.CANCEL_BUTTON}
      confirmButtonText={NAMESPACES.YES}
    />,
    document.body
  )
})

ProceedAnywayConfirmDialog.displayName = 'ProceedAnywayConfirmDialog'

const hasValidPatientId = (patientId?: number | string | null): boolean =>
  patientId !== null &&
  patientId !== undefined &&
  patientId !== '' &&
  Number(patientId) > 0

const sanitizeCaseDedupeValues = (values: FieldValues): FieldValues => {
  const sanitized: FieldValues = {}
  const excludedTopLevelKeys = ['id', '_state', 'record_status']

  Object.entries(values).forEach(([key, value]) => {
    if (excludedTopLevelKeys.includes(key)) {
      return
    }

    if (
      value === null ||
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return
    }

    if (Array.isArray(value)) {
      const firstItem = value[0]
      if (!firstItem || typeof firstItem !== 'object') return
      sanitized[key] = [firstItem]
      return
    }

    if (typeof value === 'object') {
      if ('id' in (value as Record<string, unknown>)) {
        sanitized[key] = {
          id: (value as { id?: string | number }).id,
        }
      }
      return
    }

    sanitized[key] = value
  })

  return sanitized
}

export const CaseLevelDedupeFlowModal = ({
  isOpen,
  patientId,
  caseEntityId,
  caseEntityName,
  casePayloadData,
  patientFieldName,
  programId,
  productId,
  communicationId,
  faxDetails,
  intakeEntityName,
  updateStatusToProcessing,
  onAttachCase,
  onComplete,
}: CaseLevelDedupeFlowModalProps) => {
  const needsPatientSearch = !hasValidPatientId(patientId)
  const [currentStep, setCurrentStep] = useState(0)
  const [caseListingLayout, setCaseListingLayout] =
    useState<LayoutResponseType>()
  const [totalResults, setTotalResults] = useState(0)
  const [showDedupeStep, setShowDedupeStep] = useState(false)
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false)
  const duplicatePayloadRef = useRef<ReturnType<
    typeof transformToDuplicateApiPayload
  > | null>(null)
  const selectedPatientRef = useRef<DataTypeStore | undefined>(undefined)
  const patientSearchRef = useRef<PatientAdvancedSearchPanelHandle>(null)
  const attachConfirmRef = useRef<AttachCaseConfirmDialogHandle>(null)
  const proceedConfirmRef = useRef<ConfirmDialogHandle>(null)
  const handleAttachCaseRef = useRef<(row: DataTypeStore) => void>(() => null)
  const onCompleteRef = useRef(onComplete)
  const onAttachCaseRef = useRef(onAttachCase)
  const initialDuplicateCheckRanRef = useRef(false)
  const runDuplicateCheckRef = useRef<
    (
      resolvedPatientId: number,
      selectedPatient?: DataTypeStore
    ) => Promise<void>
  >(() => Promise.resolve())

  onCompleteRef.current = onComplete
  onAttachCaseRef.current = onAttachCase

  const duplicateActions: TableActionType<DataTypeStore>[] = useMemo(
    () => [
      {
        label: NAMESPACES.ATTACH,
        key: CASE_LEVEL_DUPLICATE_ATTACH_KEY,
        showInPopUp: false,
        isEnabledOnDisabledRow: false,
        type: DYNAMIC_QUEUE_CONSTANTS.COMPONENT,
        renderActionComponent: (_row) => (
          <button
            type="button"
            title={NAMESPACES.ATTACH}
            onMouseDown={(event) => {
              event.stopPropagation()
            }}
            onClick={(event) => {
              event.stopPropagation()
              event.preventDefault()
              handleAttachCaseRef.current(_row)
            }}
            className="flex items-center justify-center font-normal text-xs w-[4.313rem] h-[1.75rem] rounded border border-primary-500 hover:bg-primary-100"
          >
            <div>{NAMESPACES.ATTACH}</div>
          </button>
        ),
      },
    ],
    []
  )

  const resetFlow = useCallback(() => {
    setCurrentStep(0)
    setCaseListingLayout(undefined)
    setTotalResults(0)
    setShowDedupeStep(false)
    setIsCheckingDuplicates(false)
    duplicatePayloadRef.current = null
    selectedPatientRef.current = undefined
    patientSearchRef.current?.reset()
    attachConfirmRef.current?.dismiss()
    proceedConfirmRef.current?.dismiss()
  }, [])

  const buildPayloadWithPatient = useCallback(
    (resolvedPatientId: number): FieldValues => {
      const data = { ...casePayloadData }

      if (patientFieldName) {
        data[patientFieldName] = {
          id: resolvedPatientId,
          _state: 'ADDED',
        }
      }

      return data
    },
    [casePayloadData, patientFieldName]
  )

  const runDuplicateCheck = useCallback(
    async (resolvedPatientId: number, selectedPatient?: DataTypeStore) => {
      if (!hasValidPatientId(resolvedPatientId) || !patientFieldName) {
        if (!needsPatientSearch) {
          onCompleteRef.current({ action: CASE_LEVEL_DEDUPE_ACTION.CANCEL })
        }
        return
      }

      setIsCheckingDuplicates(true)

      try {
        const layoutRes = await getLayoutByEntityIdAndTypeApi({
          entityId: caseEntityId.toString(),
          type: LAYOUT_TYPE_NAMESPACE.LAYOUT_LISTING,
          isDefault: true,
        })
        const listingLayout = layoutRes?.data?.[0]

        if (!listingLayout?.id) {
          onCompleteRef.current({
            action: CASE_LEVEL_DEDUPE_ACTION.PROCEED,
            selectedPatient,
          })
          return
        }

        setCaseListingLayout((previousLayout) =>
          previousLayout?.id === listingLayout.id
            ? previousLayout
            : listingLayout
        )

        const payloadWithPatient = buildPayloadWithPatient(resolvedPatientId)
        const patientValue = payloadWithPatient[patientFieldName]
        const patientIdInPayload =
          patientValue &&
          typeof patientValue === 'object' &&
          'id' in (patientValue as Record<string, unknown>)
            ? Number((patientValue as { id?: string | number }).id)
            : undefined

        if (!hasValidPatientId(patientIdInPayload)) {
          if (!needsPatientSearch) {
            onCompleteRef.current({ action: CASE_LEVEL_DEDUPE_ACTION.CANCEL })
          }
          return
        }

        const dedupeSourceValues = sanitizeCaseDedupeValues(payloadWithPatient)
        const duplicateApiPayload = transformToDuplicateApiPayload(
          dedupeSourceValues,
          caseEntityId.toString(),
          listingLayout.id.toString(),
          listingLayout
        )

        const resolvedProgramId = String(programId ?? '').trim()
        if (resolvedProgramId) {
          applyProgramScopeToDuplicatePayload(
            duplicateApiPayload,
            resolvedProgramId
          )
        }

        duplicateApiPayload.select.push('c_case_statuses')
        duplicateApiPayload.related_entities['c_case_statuses'] = {
          select: ['id', 'overall_case_status'],
          filters: [],
        }

        duplicatePayloadRef.current = duplicateApiPayload

        const duplicateRecords = await fetchDuplicateRecordsAPI(
          caseEntityId,
          duplicateApiPayload,
          0,
          FIELD_VALUE_CONSTANTS.TEN
        )

        if (duplicateRecords?.data?.content?.length > 0) {
          setTotalResults(duplicateRecords?.data?.totalElements ?? 0)
          setShowDedupeStep(true)
          if (needsPatientSearch) {
            setCurrentStep(1)
          }
        } else {
          onCompleteRef.current({
            action: CASE_LEVEL_DEDUPE_ACTION.PROCEED,
            selectedPatient,
          })
        }
      } catch (error) {
        console.error(error)
        onCompleteRef.current({ action: CASE_LEVEL_DEDUPE_ACTION.CANCEL })
      } finally {
        setIsCheckingDuplicates(false)
      }
    },
    [
      buildPayloadWithPatient,
      caseEntityId,
      needsPatientSearch,
      patientFieldName,
      programId,
    ]
  )

  runDuplicateCheckRef.current = runDuplicateCheck

  useEffect(() => {
    if (!isOpen) {
      resetFlow()
      initialDuplicateCheckRanRef.current = false
      return
    }

    if (needsPatientSearch) {
      return
    }

    if (initialDuplicateCheckRanRef.current) {
      return
    }

    if (hasValidPatientId(patientId) && patientFieldName) {
      initialDuplicateCheckRanRef.current = true
      void runDuplicateCheckRef.current(Number(patientId))
    }
  }, [isOpen, needsPatientSearch, patientId, patientFieldName, resetFlow])

  const fetchDuplicateTableDataCallback = useCallback(
    async (
      payload?: getEntityDataByEntityFilter['payload'] & { layout_id: number }
    ) => {
      if (!duplicatePayloadRef.current) {
        return {
          content: [],
          totalElements: 0,
        }
      }

      const res = await fetchDuplicateRecordsAPI(
        caseEntityId,
        duplicatePayloadRef.current,
        payload?.page as number,
        payload?.size as number
      )
      return res?.data
    },
    [caseEntityId]
  )

  const handleCancel = useCallback(() => {
    if (attachConfirmRef.current?.isOpen()) {
      attachConfirmRef.current.dismiss()
      return
    }

    if (proceedConfirmRef.current?.isOpen()) {
      proceedConfirmRef.current.dismiss()
      return
    }

    resetFlow()
    onCompleteRef.current({ action: CASE_LEVEL_DEDUPE_ACTION.CANCEL })
  }, [resetFlow])

  const handleProceedAnywayConfirmed = useCallback(() => {
    const selectedPatient = selectedPatientRef.current
    resetFlow()
    onCompleteRef.current({
      action: CASE_LEVEL_DEDUPE_ACTION.PROCEED,
      selectedPatient,
    })
  }, [resetFlow])

  const handleProceedAnyway = useCallback(() => {
    proceedConfirmRef.current?.open()
  }, [])

  const handleAttachCase = useCallback((row: DataTypeStore) => {
    attachConfirmRef.current?.open(row?.id as string | number)
  }, [])

  handleAttachCaseRef.current = handleAttachCase

  const handleAttachConfirmed = useCallback(
    (caseId: string | number) => {
      onAttachCaseRef.current?.(caseId)
      resetFlow()
      onCompleteRef.current({ action: CASE_LEVEL_DEDUPE_ACTION.CANCEL })
    },
    [resetFlow]
  )

  const handlePatientSelected = useCallback(
    (record: DataTypeStore) => {
      selectedPatientRef.current = record
      const resolvedPatientId = Number(record.record_id ?? record.id ?? 0)
      void runDuplicateCheck(resolvedPatientId, record)
    },
    [runDuplicateCheck]
  )

  const patientSearchBreadcrumbLabel = `${NAMESPACES.PATIENT_KEY} ${APPLICATION_PREFERENCES_STRINGS.ADVANCED_SEARCH}`

  const breadcrumbs = useMemo(() => {
    if (currentStep === 0) {
      return [
        {
          label: patientSearchBreadcrumbLabel,
          isActive: true,
        },
      ]
    }

    return [
      {
        label: patientSearchBreadcrumbLabel,
        onClick: () => {
          setShowDedupeStep(false)
          setCurrentStep(0)
        },
      },
      {
        label: ` / ${DUPLICATE_POPUP.DUPLICATES_FOUND}`,
        isActive: true,
      },
    ]
  }, [currentStep, patientSearchBreadcrumbLabel])

  const renderBreadcrumbs = () => (
    <div className="flex items-center">
      {breadcrumbs.map((crumb, index) => (
        <span
          key={index}
          className={`font-medium text-base font-primary ${
            crumb.onClick ? 'cursor-pointer' : ''
          } ${crumb.isActive ? 'text-primary-500' : ''}`}
          onClick={crumb.onClick}
          onKeyDown={
            crumb.onClick
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    crumb.onClick?.()
                  }
                }
              : undefined
          }
          role={crumb.onClick ? 'button' : undefined}
          tabIndex={crumb.onClick ? 0 : undefined}
        >
          {crumb.label}
        </span>
      ))}
    </div>
  )

  const mappedExtraActions = useMemo(
    () =>
      duplicateActions.map((actionConfig) => ({
        ...actionConfig,
        onClick: (row: DataTypeStore) => handleAttachCase(row),
      })),
    [duplicateActions, handleAttachCase]
  )

  const dedupeModalProps = useMemo(
    () => ({
      isOpen: showDedupeStep,
      embedded: needsPatientSearch,
      title: DUPLICATE_POPUP.DUPLICATES_FOUND,
      totalResults,
      dataFetchCallback: fetchDuplicateTableDataCallback,
      entityId: caseEntityId,
      entityName: caseEntityName,
      layout: caseListingLayout,
      extraActions: mappedExtraActions,
      onProceedAnyway: handleProceedAnyway,
      onCancel: handleCancel,
      closeModalOnOutsideClick: false,
      communicationId,
      updateStatusToProcessing,
      faxDetails,
      intakeEntityName,
      proceedButtonText: BUTTONS.CREATE_NEW_CASE,
    }),
    [
      showDedupeStep,
      needsPatientSearch,
      totalResults,
      fetchDuplicateTableDataCallback,
      caseEntityId,
      caseEntityName,
      caseListingLayout,
      mappedExtraActions,
      handleProceedAnyway,
      handleCancel,
      communicationId,
      updateStatusToProcessing,
      faxDetails,
      intakeEntityName,
    ]
  )

  const attachConfirmDialog = (
    <AttachCaseConfirmDialog
      ref={attachConfirmRef}
      onConfirm={handleAttachConfirmed}
    />
  )

  const proceedAnywayConfirmDialog = (
    <ProceedAnywayConfirmDialog
      ref={proceedConfirmRef}
      onConfirm={handleProceedAnywayConfirmed}
    />
  )

  if (!isOpen) {
    return null
  }

  if (!needsPatientSearch) {
    if (!showDedupeStep && isCheckingDuplicates) {
      return null
    }

    return (
      <>
        <DuplicatePopupModal {...dedupeModalProps} />
        {attachConfirmDialog}
        {proceedAnywayConfirmDialog}
      </>
    )
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        closeModal={handleCancel}
        width="min-w-[60vw] max-w-[70vw]"
        showCloseButton={false}
        closeModalOnOutsideClick={false}
        isDragable
      >
        <div
          className={`bg-accent-0 flex flex-col ${
            currentStep && 'min-h-[50vh]'
          }`}
        >
          {currentStep === 0 && (
            <PatientAdvancedSearchPanel
              ref={patientSearchRef}
              isActive={currentStep === 0}
              programId={programId}
              productId={productId}
              isSelectionDisabled={isCheckingDuplicates}
              breadcrumbLabel={patientSearchBreadcrumbLabel}
              onPatientSelected={handlePatientSelected}
              onCancel={handleCancel}
              onProceedToIntake={handleProceedAnyway}
            />
          )}

          {currentStep === 1 && showDedupeStep && (
            <div className="flex flex-col flex-1">
              <div className="flex justify-between items-center pb-2 mb-2">
                {renderBreadcrumbs()}
              </div>
              <DuplicatePopupModal {...dedupeModalProps} />
            </div>
          )}
        </div>
      </Modal>
      {attachConfirmDialog}
      {proceedAnywayConfirmDialog}
    </>
  )
}
